import { useState } from 'react';
import { REASONING_EFFORT_OPTIONS } from '@pcp/shared';
import type { ThinkSetting } from '@pcp/shared';
import type { KnowledgeSelection } from '@pcp/shared';
import { useChatStore, useSupportChatStore } from '../lib/stores';
import { createConversation } from '../lib/api';
import { KnowledgeSelector } from './KnowledgeSelector';
import { BottomSheet } from './BottomSheet';

interface NewChatModalProps {
  onClose: () => void;
  availableModels: Record<string, string[]>;
  /** Preselect a knowledge scope (e.g. when launched from the Documentation page). */
  initialScope?: 'simple' | 'user' | 'system';
  /** Called after a conversation is successfully created (distinct from cancel). */
  onCreated?: () => void;
}

/** Map the legacy doc scope (still used by the Documentation entry point) to a selection. */
function scopeToSelection(scope?: 'simple' | 'user' | 'system'): KnowledgeSelection {
  if (scope === 'user') return { userDocs: true };
  if (scope === 'system') return { systemDocs: true };
  return {};
}

type ChatMode = 'general' | 'support';

export function NewChatModal({ onClose, availableModels, initialScope, onCreated }: NewChatModalProps) {
  const { addSession, setActiveSessionId } = useChatStore();
  const startSupport = useSupportChatStore((s) => s.startFresh);

  const [mode, setMode] = useState<ChatMode>('general');
  const [provider, setProvider] = useState<string>('openai');
  const [model, setModel] = useState<string>(availableModels['openai']?.[0] || 'gpt-5.4-mini');
  const [think, setThink] = useState<ThinkSetting>(null);
  const [title, setTitle] = useState('');
  const [numCtx, setNumCtx] = useState<number | ''>(8192);
  const [knowledgeSelection, setKnowledgeSelection] = useState<KnowledgeSelection>(scopeToSelection(initialScope));


  const models = availableModels[provider] || [];

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const p = e.target.value;
    setProvider(p);
    const m = availableModels[p] || [];
    if (m.length > 0) {
      setModel(m[0]);
    } else {
      setModel('');
    }
    // Set provider-specific defaults
    if (p === 'ollama') {
      setThink(true);
    } else {
      setThink('medium');
    }
  };

  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    // "RealEquity B2B Support" mode launches the embedded support popover instead
    // of a normal assistant conversation — same new-conversation entry point.
    if (mode === 'support') {
      onClose();
      void startSupport();
      return;
    }
    setIsCreating(true);
    try {
      const newSessionData = {
        title: title.trim() || 'New Chat',
        messages: [],
        provider,
        model,
        think: think ?? (provider === 'ollama' ? true : 'medium'),
        numCtx: provider === 'ollama' ? (numCtx === '' ? 8192 : numCtx) : null,
        knowledgeSelection,
      };
      const newSession = await createConversation(newSessionData);
      addSession(newSession);
      setActiveSessionId(newSession.id);
      onCreated?.();
      onClose();
    } catch (e) {
      console.error('Failed to create conversation', e);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <BottomSheet
      open
      onClose={onClose}
      title="New Chat"
      width={400}
      size="default"
      padded
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px',
              color: 'var(--text-secondary)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >Cancel</button>
          <button
            onClick={handleCreate}
            disabled={(mode === 'general' && !model) || isCreating}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              background: 'var(--accent-primary)', border: 'none', borderRadius: '6px',
              color: 'var(--text-inverse)',
              opacity: ((mode === 'general' && !model) || isCreating) ? 0.5 : 1,
            }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent-primary-dim)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent-primary)'; }}
          >
            {isCreating ? 'Creating...' : mode === 'support' ? 'Start support chat' : 'Start Chat'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Conversation type — pick the embedded customer Support Chat or a normal assistant chat. */}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {(['general', 'support'] as ChatMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                flex: 1, padding: 'var(--space-2) var(--space-3)', cursor: 'pointer',
                fontSize: 'var(--text-sm)', fontWeight: 600, borderRadius: 'var(--radius-md)',
                border: '1px solid ' + (mode === m ? 'var(--accent-primary)' : 'var(--border)'),
                background: mode === m ? 'var(--accent-primary-soft)' : 'var(--bg-base)',
                color: mode === m ? 'var(--accent-primary)' : 'var(--text-secondary)',
              }}
            >
              {m === 'general' ? 'General assistant' : 'RealEquity B2B Support'}
            </button>
          ))}
        </div>

        {mode === 'support' ? (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Opens the embedded <strong>RealEquity B2B Support</strong> chat — a customer-facing
            assistant that answers from the customer-facing knowledge base, offers quick-reply
            choices, and hands off to a human when needed. It appears as a popover you can minimize
            and reopen anytime. The model is set in Settings → AI Defaults.
          </div>
        ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Name</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Debug auth flow"
            autoFocus
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
              padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          />
        </div>
        
        <KnowledgeSelector value={knowledgeSelection} onChange={setKnowledgeSelection} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Provider</label>
          <select className="select" value={provider} onChange={handleProviderChange} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
            padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
          }}>
            <option value="openai">OpenAI</option>
            <option value="ollama">Ollama</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Model</label>
          <select className="select" value={model} onChange={(e) => setModel(e.target.value)} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
            padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
          }}>
            {models.length === 0 && <option value="">No models available</option>}
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {provider === 'openai' && (model.includes('o1') || model.includes('o3') || model.includes('o4') || model.includes('gpt-5')) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Reasoning Effort</label>
            <select className="select" value={String(think ?? 'medium')} onChange={(e) => setThink(e.target.value as any)} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
              padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
            }}>
              {REASONING_EFFORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {provider === 'ollama' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Context Window (num_ctx)</label>
              <input
                type="number"
                value={numCtx}
                onChange={(e) => setNumCtx(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
                  padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Thinking Level</label>
              <select className="select" value={String(think ?? 'true')} onChange={(e) => {
                const val = e.target.value;
                if (val === 'true') setThink(true);
                else if (val === 'false') setThink(false);
                else setThink(val as any);
              }} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px',
                padding: '8px 12px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none',
              }}>
                <option value="false">Disabled</option>
                <option value="true">Standard</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </>
        )}
        </>
        )}

      </div>
    </BottomSheet>
  );
}
